'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  TrendingUp, Plus, Download, Eye, BookOpen, ArrowUpRight, ArrowDownLeft,
  Search, Filter, MoreHorizontal, X, CheckCircle, Users, DollarSign, PieChart
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

interface Investor {
  id: string;
  name: string;
  phone: string;
  email: string;
  notes: string;
  is_active: boolean;
  created_at: string;
}

interface Contribution {
  id: string;
  contributor_id: string;
  amount: number;
  contributed_on: string;
  notes: string;
  fortnight_label: string;
  investor_name?: string;
}

type ModalType = 'add' | 'contribution' | 'withdrawal' | 'ledger' | null;

export default function InvestorsPage() {
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalType>(null);
  const [selectedInvestor, setSelectedInvestor] = useState<Investor | null>(null);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', amount: '', note: '', date: new Date().toISOString().split('T')[0], selectedInvestorId: '' });

  const supabase = createClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [invResult, contResult] = await Promise.all([
      supabase.from('capital_contributors').select('*').order('created_at', { ascending: false }),
      supabase.from('capital_contributions').select('*, capital_contributors(name)').order('created_at', { ascending: false }).limit(20),
    ]);
    const invData = invResult.data;
    const contData = contResult.data;
    setInvestors(invData || []);
    const mapped = (contData || []).map((c: any) => ({
      ...c,
      investor_name: c.capital_contributors?.name || '—',
    }));
    setContributions(mapped);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPool = contributions.reduce((s, c) => s + Number(c.amount), 0);
  const activeCount = investors.filter(i => i.is_active).length;

  const filtered = investors.filter(i =>
    i.name?.toLowerCase().includes(search.toLowerCase()) ||
    i.phone?.includes(search)
  );

  const openModal = (type: ModalType, investor?: Investor) => {
    setSelectedInvestor(investor || null);
    setError(null);
    setForm({ name: '', phone: '', amount: '', note: '', date: new Date().toISOString().split('T')[0], selectedInvestorId: investor?.id || '' });
    setModal(type);
  };

  const handleSaveInvestor = async () => {
    if (!form.name.trim()) { setError('Full name is required'); return; }
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase
      .from('capital_contributors')
      .insert({ name: form.name.trim(), phone: form.phone.trim(), is_active: true });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setModal(null);
      fetchData();
    }
  };

  const handleSaveContribution = async () => {
    const investorId = form.selectedInvestorId || selectedInvestor?.id;
    if (!investorId) { setError('Please select an investor'); return; }
    if (!form.amount || Number(form.amount) <= 0) { setError('Amount must be greater than 0'); return; }
    setSaving(true);
    setError(null);

    // Generate fortnight_label from the selected date (e.g. "2026-FN01")
    const dateObj = new Date(form.date);
    const year = dateObj.getFullYear();
    const dayOfYear = Math.floor((dateObj.getTime() - new Date(year, 0, 0).getTime()) / 86400000);
    const fortnightNum = Math.ceil(dayOfYear / 14);
    const fortnightLabel = `${year}-FN${String(fortnightNum).padStart(2, '0')}`;

    const { error: insertError } = await supabase
      .from('capital_contributions')
      .insert({
        contributor_id: investorId,
        amount: Number(form.amount),
        contributed_on: form.date,
        notes: form.note.trim(),
        fortnight_label: fortnightLabel,
      });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setModal(null);
      fetchData();
    }
  };

  const handleSave = () => {
    if (modal === 'add') return handleSaveInvestor();
    if (modal === 'contribution' || modal === 'withdrawal') return handleSaveContribution();
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Investors</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Capital Pool Manager — track contributions and profit shares</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => openModal('add')} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
              <Plus size={16} /> Add Investor
            </button>
            <button onClick={() => openModal('contribution')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <ArrowUpRight size={16} className="text-positive" /> Record Contribution
            </button>
            <button onClick={() => openModal('withdrawal')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <ArrowDownLeft size={16} className="text-warning" /> Record Withdrawal
            </button>
            <button onClick={() => openModal('ledger')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <BookOpen size={16} /> View Ledger
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Download size={16} /> Export Report
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-positive/10 flex items-center justify-center"><DollarSign size={16} className="text-positive" /></div>
              <span className="text-xs text-muted-foreground font-medium">Total Pool</span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatVT(totalPool)}</p>
            <p className="text-xs text-muted-foreground mt-1">Combined capital</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-info/10 flex items-center justify-center"><Users size={16} className="text-info" /></div>
              <span className="text-xs text-muted-foreground font-medium">Active Investors</span>
            </div>
            <p className="text-xl font-bold text-foreground">{activeCount}</p>
            <p className="text-xs text-muted-foreground mt-1">of {investors.length} total</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center"><TrendingUp size={16} className="text-warning" /></div>
              <span className="text-xs text-muted-foreground font-medium">Contributions</span>
            </div>
            <p className="text-xl font-bold text-foreground">{contributions.length}</p>
            <p className="text-xs text-muted-foreground mt-1">total transactions</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"><PieChart size={16} className="text-primary" /></div>
              <span className="text-xs text-muted-foreground font-medium">Avg Share</span>
            </div>
            <p className="text-xl font-bold text-foreground">{investors.length > 0 ? (100 / investors.length).toFixed(1) : '0'}%</p>
            <p className="text-xs text-muted-foreground mt-1">per investor avg</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search investors..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Filter size={15} /> Filter
          </button>
        </div>

        {/* Investors Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Investor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">Loading investors...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">No investors found. Add your first investor.</td></tr>
                ) : filtered.map(inv => (
                  <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary">{inv.name?.split(' ').map((n: string) => n[0]).join('') || '?'}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-foreground">{inv.name}</p>
                          <p className="text-xs text-muted-foreground">Since {inv.created_at?.split('T')[0]}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{inv.phone || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${inv.is_active ? 'bg-positive/10 text-positive' : 'bg-muted text-muted-foreground'}`}>
                        {inv.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openModal('contribution', inv)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-positive transition-colors" title="Record Contribution">
                          <ArrowUpRight size={14} />
                        </button>
                        <button onClick={() => openModal('ledger')} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View Ledger">
                          <Eye size={14} />
                        </button>
                        <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="More">
                          <MoreHorizontal size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Capital Contribution Log */}
        <div className="bg-card border border-border rounded-xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="font-semibold text-foreground text-sm">Recent Capital Movements</h2>
            <span className="text-xs text-muted-foreground">Linked to General Ledger</span>
          </div>
          {contributions.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground text-sm">No contributions recorded yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {contributions.map(log => (
                <div key={log.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-positive/10">
                      <ArrowUpRight size={14} className="text-positive" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{log.investor_name}</p>
                      <p className="text-xs text-muted-foreground">{log.notes || 'Contribution'} · {log.contributed_on}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-sm text-positive">+{formatVT(Number(log.amount))}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-positive/10 text-positive">Capital Contribution</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-bold text-foreground">
                {modal === 'add' && '+ Add New Investor'}
                {modal === 'contribution' && 'Record Contribution'}
                {modal === 'withdrawal' && 'Record Withdrawal'}
                {modal === 'ledger' && 'Capital Ledger'}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>

            {modal === 'ledger' ? (
              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {contributions.length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-4">No contributions yet.</p>
                ) : contributions.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="text-sm font-medium text-foreground">{log.investor_name}</p>
                      <p className="text-xs text-muted-foreground">Contribution · {log.contributed_on}</p>
                    </div>
                    <span className="font-mono text-sm font-semibold text-positive">+{formatVT(Number(log.amount))}</span>
                  </div>
                ))}
                <button onClick={() => setModal(null)} className="w-full mt-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Close</button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {error && <div className="p-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">{error}</div>}
                {modal === 'add' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Full Name *</label>
                      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Investor full name" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Phone Number</label>
                      <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+678 555 0000" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                  </>
                )}
                {(modal === 'contribution' || modal === 'withdrawal') && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Investor *</label>
                      <select
                        value={form.selectedInvestorId}
                        onChange={e => setForm(f => ({ ...f, selectedInvestorId: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">Select investor...</option>
                        {investors.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Amount (VT) *</label>
                      <input value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" type="number" min="1" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Date</label>
                      <input value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} type="date" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Note</label>
                      <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Optional note" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                  </>
                )}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {saving ? 'Saving...' : <><CheckCircle size={15} /> Save</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
